import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  THead,
  TBody,
  Th,
  Tr,
  Td,
  Badge,
  Spinner,
  Text,
} from "@twilio-paste/core";
import { PlusIcon } from "@twilio-paste/icons/esm/PlusIcon";
import { EditIcon } from "@twilio-paste/icons/esm/EditIcon";
import { TemplateDto } from "../../dto/template";
import { TeamDto } from "../../dto/team";
import { TemplateCategoryDto } from "../../dto/templateCategory";
import {
  fetchAllTemplates,
  fetchTeams,
  fetchTemplateCategories,
  updateTemplate,
} from "../../api/adminApi";
import TemplateFormModal from "./TemplateFormModal";

const TemplatesPanel: React.FC = () => {
  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [categories, setCategories] = useState<TemplateCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateDto | null>(
    null
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [tmplts, ts, cats] = await Promise.all([
          fetchAllTemplates(),
          fetchTeams(),
          fetchTemplateCategories(),
        ]);
        setTemplates(tmplts);
        setTeams(ts);
        setCategories(cats);
      } catch {
        setError("Errore nel caricamento dei template.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const handleEditTemplate = (t: TemplateDto) => {
    setEditingTemplate(t);
    setModalOpen(true);
  };

  const handleToggleActive = async (t: TemplateDto) => {
    try {
      const updated = await updateTemplate(t.id, {
        name: t.name,
        content: t.content,
        templateSid: t.templateSid,
        teamIds: t.teams.map((x) => x.id),
        variables: [...t.variables],
        categoryId: t.categoryId || null,
        isActive: !t.isActive,
      });
      setTemplates((prev) =>
        prev.map((x) => (x.id === updated.id ? updated : x))
      );
    } catch {
      setError("Errore durante l'aggiornamento del template.");
    }
  };

  const handleSaved = (saved: TemplateDto) => {
    setTemplates((prev) => {
      const exists = prev.find((t) => t.id === saved.id);
      if (exists) return prev.map((t) => (t.id === saved.id ? saved : t));
      return [...prev, saved];
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding="space80">
        <Spinner decorative={false} title="Caricamento template..." />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="space50"
      >
        <Text as="h4" fontSize="fontSize40" fontWeight="fontWeightBold">
          Template ({templates.length})
        </Text>
        <Button variant="primary" onClick={handleNewTemplate}>
          <PlusIcon decorative />
          Nuovo template
        </Button>
      </Box>

      {error && (
        <Box marginBottom="space40">
          <Text as="p" color="colorTextError">
            {error}
          </Text>
        </Box>
      )}

      <Table>
        <THead>
          <Tr>
            <Th>Nome</Th>
            <Th>Categoria</Th>
            <Th>Team</Th>
            <Th>Variabili</Th>
            <Th>Stato</Th>
            <Th>Azioni</Th>
          </Tr>
        </THead>
        <TBody>
          {templates.map((t) => (
            <Tr key={t.id}>
              <Td>{t.name}</Td>
              <Td>{t.categoryName || "—"}</Td>
              <Td>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  columnGap="space20"
                  rowGap="space10"
                >
                  {t.teams.map((team) => (
                    <Badge key={team.id} as="span" variant="neutral">
                      {team.name}
                    </Badge>
                  ))}
                  {t.teams.length === 0 && "—"}
                </Box>
              </Td>
              <Td>
                {t.variables.length > 0
                  ? t.variables.map((v) => v.key).join(", ")
                  : "—"}
              </Td>
              <Td>
                <Badge as="span" variant={t.isActive ? "success" : "neutral"}>
                  {t.isActive ? "Attivo" : "Disabilitato"}
                </Badge>
              </Td>
              <Td>
                <Box display="flex" columnGap="space30">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => handleEditTemplate(t)}
                  >
                    <EditIcon decorative />
                    Modifica
                  </Button>
                  <Button
                    variant={t.isActive ? "destructive_secondary" : "secondary"}
                    size="small"
                    onClick={() => handleToggleActive(t)}
                  >
                    {t.isActive ? "Disabilita" : "Abilita"}
                  </Button>
                </Box>
              </Td>
            </Tr>
          ))}
          {templates.length === 0 && (
            <Tr>
              <Td colSpan={6}>
                <Text as="p" color="colorTextWeak" textAlign="center">
                  Nessun template trovato.
                </Text>
              </Td>
            </Tr>
          )}
        </TBody>
      </Table>

      <TemplateFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        template={editingTemplate}
        teams={teams}
        categories={categories}
      />
    </Box>
  );
};

export default TemplatesPanel;
